import {
  type UseMutationResult,
  type UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useCallback } from 'react';

import {
  type CompanyInvitation,
  type CompanyInvitationCreate,
  type CompanyUser,
  type CompanyUserUpdate,
  createCompanyInvitation,
  fetchCompanyInvitations,
  fetchCompanyUsers,
  resendCompanyInvitation,
  revokeCompanyInvitation,
  updateCompanyUser,
} from '~/api/companyUsers';
import type { ScopedQueryOptions } from '~/api/hooks/useInvoices';
import { queryKeys } from '~/api/queryKeys';

/**
 * Lança para a `ErrorBoundary` só quando não há nada para mostrar. A tela vive das listas do
 * cache persistido: uma revalidação que falha (offline) tem de manter o que já está na tela, e
 * isso não pode depender de como o `throwOnError: true` global trata um erro com dado em mão.
 */
function onlyWithoutCachedData(_error: Error, query: { state: { data: unknown } }): boolean {
  return query.state.data === undefined;
}

/**
 * A equipe da empresa em operação. `enabled` desliga a busca quando a conta não tem
 * `company_users` `FULL` nela — sem isso o backend responderia 403 e a tela cairia no error
 * boundary.
 */
export function useCompanyUsersQuery(
  clientId: number,
  options: ScopedQueryOptions = {},
): UseQueryResult<CompanyUser[]> {
  return useQuery({
    queryKey: queryKeys.companyUsers.list(clientId),
    queryFn: () => fetchCompanyUsers(clientId),
    enabled: options.enabled ?? true,
    throwOnError: onlyWithoutCachedData,
  });
}

/** Os convites em aberto da empresa: pendentes e vencidos, que ainda ocupam o e-mail. */
export function useCompanyInvitationsQuery(
  clientId: number,
  options: ScopedQueryOptions = {},
): UseQueryResult<CompanyInvitation[]> {
  return useQuery({
    queryKey: queryKeys.companyInvitations.list(clientId),
    queryFn: () => fetchCompanyInvitations(clientId),
    enabled: options.enabled ?? true,
    throwOnError: onlyWithoutCachedData,
  });
}

/**
 * Toda escrita na equipe muda as duas listas: o convite aceito sai dos convites e entra nos
 * vínculos, e o vínculo desativado muda o que a lista mostra.
 */
function useTeamRefresh(clientId: number): () => Promise<void> {
  const queryClient = useQueryClient();
  return useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.companyUsers.list(clientId) }),
      queryClient.invalidateQueries({ queryKey: queryKeys.companyInvitations.list(clientId) }),
    ]);
  }, [clientId, queryClient]);
}

export interface CompanyUserChange {
  id: number;
  body: CompanyUserUpdate;
}

export function useUpdateCompanyUser(
  clientId: number,
): UseMutationResult<CompanyUser, Error, CompanyUserChange> {
  const refresh = useTeamRefresh(clientId);
  return useMutation({
    mutationFn: ({ id, body }: CompanyUserChange) => updateCompanyUser(clientId, id, body),
    onSuccess: refresh,
  });
}

export function useCreateCompanyInvitation(
  clientId: number,
): UseMutationResult<CompanyInvitation, Error, CompanyInvitationCreate> {
  const refresh = useTeamRefresh(clientId);
  return useMutation({
    mutationFn: (body: CompanyInvitationCreate) => createCompanyInvitation(clientId, body),
    onSuccess: refresh,
  });
}

export function useResendCompanyInvitation(
  clientId: number,
): UseMutationResult<CompanyInvitation, Error, number> {
  const refresh = useTeamRefresh(clientId);
  return useMutation({
    mutationFn: (id: number) => resendCompanyInvitation(clientId, id),
    onSuccess: refresh,
  });
}

export function useRevokeCompanyInvitation(
  clientId: number,
): UseMutationResult<void, Error, number> {
  const refresh = useTeamRefresh(clientId);
  return useMutation({
    mutationFn: (id: number) => revokeCompanyInvitation(clientId, id),
    onSuccess: refresh,
  });
}
